import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import documentService from '../services/documentService';
import { useNotification } from '../context/NotificationContext';
import AuthContext from '../context/AuthContext';
import { formatInstantDate } from '../utils/datetime';
import ReplaceDocumentModal from '../components/ReplaceDocumentModal';
import '../styles/general.css';

/**
 * The ten company document slots.
 *
 * No filters, sorting or pagination: the list is a fixed catalogue of ten, so
 * every one of those would be furniture around a table that always fits on
 * one screen.
 *
 * Empty slots are rendered rather than skipped. Nine of ten rows are empty on
 * day one, and a list that hid them would give the user nowhere to upload.
 */
const DocumentsListPage = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { timezone, canManage } = useContext(AuthContext);
  // Replacing is hidden, not disabled: Ventas and Compras read and download,
  // and a button they can never enable is its own kind of noise.
  const canWrite = canManage && canManage('documents');

  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [downloadingSlug, setDownloadingSlug] = useState(null);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [replacingSlug, setReplacingSlug] = useState(null);

  const loadSlots = useCallback(async () => {
    try {
      setLoading(true);
      const response = await documentService.listDocuments();
      setSlots(response.data || []);
    } catch (err) {
      console.error('Error loading company documents:', err);
      notify.error(t('files.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [notify, t]);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  const filled = slots.filter((slot) => slot.has_file);
  const allSelected = filled.length > 0 && selected.length === filled.length;

  const toggleSlot = (slug) => {
    setSelected((current) =>
      current.includes(slug) ? current.filter((s) => s !== slug) : [...current, slug]
    );
  };

  const toggleAll = () => {
    setSelected(allSelected ? [] : filled.map((slot) => slot.slug));
  };

  const handleDownload = async (slug) => {
    setDownloadingSlug(slug);
    try {
      await documentService.downloadDocument(slug);
    } catch (err) {
      console.error('Error downloading company document:', err);
      notify.error(t('files.failedToDownload'));
    } finally {
      setDownloadingSlug(null);
    }
  };

  const handleDownloadAll = async () => {
    setDownloadingZip(true);
    try {
      await documentService.downloadZip(selected);
    } catch (err) {
      console.error('Error downloading company documents zip:', err);
      notify.error(t('files.failedToDownload'));
    } finally {
      setDownloadingZip(false);
    }
  };

  const handleReplaced = () => {
    setReplacingSlug(null);
    notify.success(t('files.replaced'));
    loadSlots();
  };

  const describe = (slot) => {
    if (!slot.has_file) return t('files.noFile');
    return `${slot.extension.toUpperCase()} · ${formatInstantDate(slot.updated_at, timezone)}`;
  };

  if (loading && slots.length === 0) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <div className="page-container-wide">
      <div className="page-header">
        <h1>{t('files.title')}</h1>
        <button
          onClick={handleDownloadAll}
          disabled={selected.length === 0 || downloadingZip}
          className="btn btn-export"
        >
          {t('files.downloadAll')}
        </button>
      </div>

      <div className="table-wrapper">
        <table className="table">
          <thead>
            <tr>
              <th className="text-center">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={filled.length === 0}
                  aria-label={t('files.selectAll')}
                />
              </th>
              <th>{t('files.headers.name')}</th>
              <th className="text-center">{t('files.headers.status')}</th>
              <th className="text-center">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {slots.map((slot) => (
              <tr key={slot.slug}>
                <td className="text-center">
                  {slot.has_file && (
                    <input
                      type="checkbox"
                      checked={selected.includes(slot.slug)}
                      onChange={() => toggleSlot(slot.slug)}
                      aria-label={t('files.select')}
                    />
                  )}
                </td>
                <td>{t(`files.documents.${slot.slug}`)}</td>
                <td className="text-center">{describe(slot)}</td>
                <td className="text-center table-actions">
                  {slot.has_file && (
                    <button
                      onClick={() => handleDownload(slot.slug)}
                      disabled={downloadingSlug === slot.slug}
                      className="btn btn-export btn-small"
                    >
                      {t('files.download')}
                    </button>
                  )}
                  {canWrite && (
                    <button
                      onClick={() => setReplacingSlug(slot.slug)}
                      className="btn btn-primary btn-small"
                    >
                      {t('files.replace')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ReplaceDocumentModal
        slug={replacingSlug}
        onClose={() => setReplacingSlug(null)}
        onReplaced={handleReplaced}
      />
    </div>
  );
};

export default DocumentsListPage;
